import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { computed } from 'mobx';
import { Dialog, Card, Text, Box } from 'core/components';
import Collection from 'core/model/Collection';
import { Model } from 'core/model';
import PolicyLink from 'app/components/links/PolicyLink';
import Link from 'core/components/Link';
import { CELL_TYPES, Table } from 'core/components/table';
import { RemoveButton } from 'core/form';
import { getChannelUsageMap } from '@kentik/ui-shared/notifications/notificationUtilities';

@inject('$auth', '$insights', '$alerting', '$mitigations', '$syn', '$mkp')
@observer
export default class NotificationUsageListContainer extends Component {
  renderTwoLineCell = (mainValue, subLabel) => (
    <Box>
      <Text as="div">{mainValue}</Text>
      <Text as="div" small muted>
        {subLabel}
      </Text>
    </Box>
  );

  get columns() {
    const { $auth, addDeleteColumn, notificationModel } = this.props;

    const finalColumns = [
      {
        label: 'Source',
        name: 'where',
        width: 120
      },
      {
        label: 'ID',
        name: 'id',
        width: 180,
        renderer: ({ model }) => {
          const subpolicyId = model.get('subpolicyId');
          const tenantId = model.get('tenantId');

          if (subpolicyId && tenantId) {
            return this.renderTwoLineCell(subpolicyId, `Tenant: ${tenantId}`);
          }

          return model.get('id');
        }
      },
      {
        label: 'Name',
        name: 'name',
        renderer: ({ model, value }) => {
          const tenantName = model.get('tenantName');

          if (tenantName) {
            return this.renderTwoLineCell(value, `Tenant: ${tenantName}`);
          }

          return value;
        }
      },
      {
        label: 'Link',
        name: 'link',
        width: 160,
        sortable: false,
        renderer: ({ model, value }) => {
          const source = model.get('where');
          const linkProps = {
            target: '_blank',
            to: value,
            rel: 'noopener noreferrer'
          };

          if (source.includes('Auto Investigation')) {
            return <Link {...linkProps}>Go to Auto Investigation</Link>;
          }

          if (source.includes('Tenant')) {
            return <Link {...linkProps}>Edit Tenant</Link>;
          }

          // TODO: redirect to levels tab for NMS policies https://github.com/kentik/ui-app/issues/24842
          if (source.includes('Policy')) {
            return <PolicyLink {...linkProps} id={model.get('id')} />;
          }

          if (source.includes('Mitigation')) {
            return <Link {...linkProps}>Edit Mitigation Method</Link>;
          }

          if (source.includes('Family')) {
            return <Link {...linkProps}>Go to Insights</Link>;
          }

          if (source.includes('Insight')) {
            return <Link {...linkProps}>Go to Insight</Link>;
          }

          if (source.includes('Synth')) {
            return <Link {...linkProps}>Go to Test</Link>;
          }

          return value;
        }
      }
    ];

    // this auth permission check is a little iffy - technically users are editing other entities
    // that the notifications are tied to (ie. policies, tests etc.) and not the notifications themselves
    // it's too difficult to check what they're editing and disable based on permissions for those, so we just go with this
    if (addDeleteColumn && $auth.hasRbacPermissions(['alerting.notifications::update'])) {
      finalColumns.push({
        type: CELL_TYPES.ACTION,
        actions: [
          (associationModel) => (
            <RemoveButton
              popoverProps={{
                confirmBodyContent: 'Are you sure you want this channel to stop notifications from this source?'
              }}
              key="delete"
              showIcon
              hideText
              onRemove={() => notificationModel.removeAssociation(associationModel)}
              small
            />
          )
        ]
      });
    }

    return finalColumns;
  }

  @computed
  get notificationUsageMap() {
    const { $alerting, $mitigations, $syn, $mkp, notificationModel } = this.props;
    return getChannelUsageMap(
      $alerting.policyCollection,
      $mitigations.methodsCollection,
      $syn.tests,
      $mkp.tenants,
      notificationModel
    );
  }

  @computed
  get usageCollection() {
    const { $alerting, $mitigations, $syn, $mkp } = this.props;
    const usages = this.notificationUsageMap;

    const collection = new Collection([]);

    Object.keys(usages).forEach((usageKey) => {
      const usage = usages[usageKey];

      switch (usageKey) {
        case 'mitigationMethods': {
          const methodModels = usage
            .map((methodId) => {
              const mitigationMethod = $mitigations.methodsCollection.modelById[methodId];

              if (mitigationMethod) {
                return new Model({
                  where: 'Mitigation Method',
                  lhs: 'mitigationMethod',
                  id: methodId,
                  name: mitigationMethod.get('name'),
                  link: `/v4/settings/mitigations/methods/${methodId}`
                });
              }

              return null;
            })
            .filter(Boolean);

          collection.add(methodModels);

          break;
        }
        case 'insights': {
          const { $insights } = this.props;
          const insightModels = usage.map((insightName) => {
            const insightOption = $insights.getInsightNames().find((option) => option.value === insightName);
            return new Model({
              where: 'Insight',
              lhs: 'insightName',
              id: insightName,
              name: insightOption?.label || insightName,
              link: `/v4/core/insights?insightNames=${insightName}`
            });
          });

          collection.add(insightModels);

          break;
        }
        case 'insightFamilies': {
          const insightFamilyModels = usage.map(
            (insightFamilyName) =>
              new Model({
                where: 'Insight Family',
                lhs: 'insightFamily',
                id: '---',
                name: insightFamilyName,
                link: `/v4/core/insights?families=${insightFamilyName}`
              })
          );

          collection.add(insightFamilyModels);

          break;
        }

        case 'synthTest': {
          const testModels = Object.keys(usage)
            .map((testId) => {
              const { tests } = $syn;
              const containingTest = tests.modelById[testId];

              if (containingTest) {
                return new Model({
                  where: 'Synthetic Test',
                  lhs: 'alertmanRule',
                  id: testId,
                  associationId: containingTest.get('config.alerting.ktrac_rule_id'),
                  name: containingTest.attributes.display_name,
                  link: `/v4/synthetics/tests/${testId}`
                });
              }

              return null;
            })
            .filter(Boolean);

          collection.add(testModels);

          break;
        }

        case 'policies': {
          const policyModels = Object.keys(usage)
            .map((policyId) => {
              const { policyCollection } = $alerting;
              const containingPolicy = policyCollection.modelById[policyId];

              if (containingPolicy) {
                return new Model({
                  where: 'Alert Policy',
                  lhs: 'alertPolicy',
                  id: containingPolicy.id,
                  associationId: containingPolicy.ruleId,
                  name: containingPolicy.get('name'),
                  link: containingPolicy.manageUrl
                });
              }

              return null;
            })
            .filter(Boolean);

          collection.add(policyModels);

          break;
        }

        case 'subpolicies': {
          // Subpolicies usage shape: Record<tenantId, subpolicyId[]>
          const tenantIds = Object.keys(usage);

          const subpolicyModels = tenantIds.flatMap((tenantId) => {
            const tenantModel = $mkp.tenants.get(tenantId);
            const subpolicyIds = usage[tenantId];
            const uniqueSubpolicyIds = [...new Set(subpolicyIds)];

            return uniqueSubpolicyIds.map((subpolicyId) => {
              const matchingSubpolicy = tenantModel?.subpolicies?.find(({ id }) => `${id}` === `${subpolicyId}`);
              const subpolicyRuleId = matchingSubpolicy?.activationSettings?.ruleId;

              return new Model({
                where: 'Tenant Policy',
                lhs: 'alertmanRule',
                id: subpolicyRuleId,
                subpolicyId,
                tenantId,
                tenantName: tenantModel?.name,
                name: matchingSubpolicy?.name,
                link: `/v4/mkp/tenants/${tenantId}`
              });
            });
          });

          collection.add(subpolicyModels);

          break;
        }

        case 'aiInvestigation': {
          const aiModels = Object.keys(usage).map((ruleId) => {
            const policyName = $alerting.policyCollection.getPolicyByRuleId(ruleId)?.get('name') ?? ruleId;

            return new Model({
              where: 'Auto Investigation',
              requiredSelectors: [
                { lhs: 'alertmanRule', rhs: ruleId },
                { lhs: 'eventType', rhs: 'aiInvestigationEvent' }
              ],
              id: ruleId,
              name: policyName,
              link: '/v4/settings/kentik-ai/auto-investigation'
            });
          });

          collection.add(aiModels);

          break;
        }

        case 'unknown': {
          const unknownModels = usage.map(
            (item, idx) =>
              new Model({
                where: 'Other',
                requiredSelectors: item.lhs ? [{ lhs: item.lhs, rhs: item.rhs }] : [],
                id: `unknown-${idx}`,
                name: item.lhs ? `${item.lhs} = ${item.rhs}` : 'Unknown',
                link: ''
              })
          );

          collection.add(unknownModels);

          break;
        }

        // Do nothing, unexpected case
        default:
      }
    });

    return collection;
  }

  render() {
    const { $alerting, $mitigations, $syn, $mkp, asTable, loading, isOpen, onClose } = this.props;
    const { policyCollection } = $alerting;
    const { methodsCollection } = $mitigations;
    const isLoading =
      loading ||
      !policyCollection.hasFetched ||
      !methodsCollection.hasFetched ||
      !$syn.tests.hasFetched ||
      !$mkp.tenants.hasFetched;

    const table = (
      <Card width="100%">
        <Table loading={isLoading} collection={this.usageCollection} columns={this.columns} selectOnRowClick={false} />
      </Card>
    );

    if (asTable) {
      return table;
    }

    return (
      <Dialog
        isOpen={isOpen}
        onClose={onClose}
        title="Notification Channel Usage Detail"
        canEscapeKeyClose
        canClickOutside
        isCloseButtonShown
        width={800}
        maxHeight={500}
        minHeight={300}
      >
        <Dialog.Body>{table}</Dialog.Body>
      </Dialog>
    );
  }
}
